/**
 *  @class
 *  @function UniversalImageText
 */

if (!customElements.get('universal-image-text')) {
  class UniversalImageText extends HTMLElement {
    constructor() {
      super();

      this.tl = false;
      this.splittext = false;
      this.svgObserver = null;
    }

    connectedCallback() {
      this.autoLinkUrls();

      // Initialize slider if present
      this.initSlider();

      // Initialize SVG animation on scroll
      this.initSvgAnimation();

      // Initialize pillars scroll animation
      this.initPillarsScroll();

      // Initialize text animations if enabled
      if (document.body.classList.contains('animations-true') && typeof gsap !== 'undefined') {
        this.prepareAnimations();
      }
    }

    disconnectedCallback() {
      if (document.body.classList.contains('animations-true') && typeof gsap !== 'undefined') {
        if (this.tl) {
          this.tl.kill();
        }
        if (this.splittext) {
          this.splittext.revert();
        }
      }

      if (this.svgObserver) {
        this.svgObserver.disconnect();
      }

      if (this._pillarsScrollHandler) {
        window.removeEventListener('scroll', this._pillarsScrollHandler);
      }

      if (this._pillarsVisibilityObserver) {
        this._pillarsVisibilityObserver.disconnect();
      }
    }

    initSlider() {
      const sliderWrapper = this.querySelector('.uit-slider--wrapper');
      if (!sliderWrapper) return;

      const track = sliderWrapper.querySelector('.uit-slider--track');
      const textSlides = sliderWrapper.querySelectorAll('.uit-slider--text-slide');
      const imageSlides = sliderWrapper.querySelectorAll('.uit-slider--image-slide');
      const prevBtn = sliderWrapper.querySelector('.uit-slider--arrow-prev');
      const nextBtn = sliderWrapper.querySelector('.uit-slider--arrow-next');
      const paginationContainer = sliderWrapper.querySelector('.uit-slider--pagination');

      if (!track || textSlides.length === 0) return;

      let currentIndex = 0;
      const totalSlides = textSlides.length;

      // Create pagination dots
      if (paginationContainer) {
        paginationContainer.innerHTML = '';
        for (let i = 0; i < totalSlides; i++) {
          const dot = document.createElement('button');
          dot.className = 'uit-slider--pagination-dot' + (i === 0 ? ' active' : '');
          dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
          dot.addEventListener('click', () => goToSlide(i));
          paginationContainer.appendChild(dot);
        }
      }

      const dots = paginationContainer ? paginationContainer.querySelectorAll('.uit-slider--pagination-dot') : [];

      function updateSlider() {
        // Slide text
        track.style.transform = `translateX(-${currentIndex * 100}%)`;

        // Fade images (desktop)
        imageSlides.forEach((img, i) => {
          img.classList.toggle('is-active', i === currentIndex);
        });

        // Update arrows
        if (prevBtn) prevBtn.disabled = currentIndex === 0;
        if (nextBtn) nextBtn.disabled = currentIndex >= totalSlides - 1;

        // Update dots
        dots.forEach((dot, i) => {
          dot.classList.toggle('active', i === currentIndex);
        });
      }

      function goToSlide(index) {
        currentIndex = Math.max(0, Math.min(index, totalSlides - 1));
        updateSlider();
      }

      function prev() {
        if (currentIndex > 0) {
          currentIndex--;
          updateSlider();
        }
      }

      function next() {
        if (currentIndex < totalSlides - 1) {
          currentIndex++;
          updateSlider();
        }
      }

      // Button events
      if (prevBtn) prevBtn.addEventListener('click', prev);
      if (nextBtn) nextBtn.addEventListener('click', next);

      // Touch/drag support
      let isDragging = false;
      let startX = 0;
      let currentX = 0;
      let cachedContainerWidth = 0; // Cache width to avoid forced reflow during drag

      const trackContainer = sliderWrapper.querySelector('.uit-slider--track-container');

      trackContainer.addEventListener('touchstart', (e) => {
        isDragging = true;
        startX = e.touches[0].pageX;
        cachedContainerWidth = trackContainer.offsetWidth; // Cache once at drag start
        track.style.transition = 'none';
      }, { passive: true });

      trackContainer.addEventListener('touchmove', (e) => {
        if (!isDragging) return;
        currentX = e.touches[0].pageX;
        const diff = currentX - startX;
        const baseTranslate = -currentIndex * 100;
        const movePercent = (diff / cachedContainerWidth) * 100;
        track.style.transform = `translateX(${baseTranslate + movePercent}%)`;
      }, { passive: true });

      trackContainer.addEventListener('touchend', () => {
        if (!isDragging) return;
        isDragging = false;
        track.style.transition = '';

        const diff = currentX - startX;
        const threshold = cachedContainerWidth * 0.2;

        if (Math.abs(diff) > threshold) {
          if (diff > 0 && currentIndex > 0) {
            prev();
          } else if (diff < 0 && currentIndex < totalSlides - 1) {
            next();
          } else {
            updateSlider();
          }
        } else {
          updateSlider();
        }
      });

      // Mouse drag support
      trackContainer.addEventListener('mousedown', (e) => {
        isDragging = true;
        startX = e.pageX;
        cachedContainerWidth = trackContainer.offsetWidth; // Cache once at drag start
        track.style.transition = 'none';
        e.preventDefault();
      });

      trackContainer.addEventListener('mousemove', (e) => {
        if (!isDragging) return;
        currentX = e.pageX;
        const diff = currentX - startX;
        const baseTranslate = -currentIndex * 100;
        const movePercent = (diff / cachedContainerWidth) * 100;
        track.style.transform = `translateX(${baseTranslate + movePercent}%)`;
      });

      trackContainer.addEventListener('mouseup', () => {
        if (!isDragging) return;
        isDragging = false;
        track.style.transition = '';

        const diff = currentX - startX;
        const threshold = cachedContainerWidth * 0.2;

        if (Math.abs(diff) > threshold) {
          if (diff > 0 && currentIndex > 0) {
            prev();
          } else if (diff < 0 && currentIndex < totalSlides - 1) {
            next();
          } else {
            updateSlider();
          }
        } else {
          updateSlider();
        }
      });

      trackContainer.addEventListener('mouseleave', () => {
        if (isDragging) {
          isDragging = false;
          track.style.transition = '';
          updateSlider();
        }
      });

      // Initial update
      updateSlider();
    }

    autoLinkUrls() {
      const rteElements = this.querySelectorAll('.rte');
      const urlPattern = /(https?:\/\/[^\s<]+)/g;

      rteElements.forEach(rte => {
        const walker = document.createTreeWalker(rte, NodeFilter.SHOW_TEXT, null, false);
        const textNodes = [];

        while (walker.nextNode()) {
          if (urlPattern.test(walker.currentNode.textContent)) {
            textNodes.push(walker.currentNode);
          }
          urlPattern.lastIndex = 0;
        }

        textNodes.forEach(textNode => {
          const text = textNode.textContent;
          const fragment = document.createDocumentFragment();
          let lastIndex = 0;
          let match;

          urlPattern.lastIndex = 0;
          while ((match = urlPattern.exec(text)) !== null) {
            if (match.index > lastIndex) {
              fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
            }
            const link = document.createElement('a');
            link.href = match[0];
            link.textContent = match[0];
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            fragment.appendChild(link);
            lastIndex = urlPattern.lastIndex;
          }

          if (lastIndex < text.length) {
            fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
          }

          textNode.parentNode.replaceChild(fragment, textNode);
        });
      });
    }

    initPillarsScroll() {
      const pillarsContainer = this.querySelector('.uit-pillars--image-container');
      if (!pillarsContainer) return;

      const pillars = pillarsContainer.querySelectorAll('.uit-pillars--pillar');
      if (!pillars.length) return;

      // Check if GSAP is available
      const hasGsap = typeof gsap !== 'undefined';

      // Image center is at 50%, 50% of the container
      const centerX = 50;
      const centerY = 50;

      // Store orbital parameters for each pillar
      const isMobile = window.innerWidth < 1024;
      const unit = isMobile ? 'px' : '%';

      let pillarsInitialized = false;
      const initPillarParams = () => {
        if (pillarsInitialized) return;
        pillarsInitialized = true;

        // For mobile (px), compute center from container dimensions
        let mobileCenterX, mobileCenterY;
        if (isMobile) {
          const rect = pillarsContainer.getBoundingClientRect();
          mobileCenterX = rect.width / 2;
          mobileCenterY = rect.height / 2;
        }

        pillars.forEach((pillar) => {
          if (!pillar._cachedStyle) {
            pillar._cachedStyle = getComputedStyle(pillar);
          }
          const style = pillar._cachedStyle;

          const posX = parseFloat(style.getPropertyValue(isMobile ? '--pillar-x-mobile' : '--pillar-x-desktop')) || (isMobile ? 0 : 50);
          const posY = parseFloat(style.getPropertyValue(isMobile ? '--pillar-y-mobile' : '--pillar-y-desktop')) || (isMobile ? 0 : 50);

          const cx = isMobile ? mobileCenterX : centerX;
          const cy = isMobile ? mobileCenterY : centerY;

          const deltaX = posX - cx;
          const deltaY = posY - cy;
          pillar._orbitRadius = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
          pillar._startAngle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);
          pillar._orbitSpeed = parseFloat(pillar.dataset.orbitSpeed) || 15;
          pillar._centerX = cx;
          pillar._centerY = cy;
          pillar._currentAngle = pillar._startAngle;

          // Enable GPU acceleration
          pillar.style.willChange = 'left, top';
          pillar.style.transform = 'translate3d(-50%, -50%, 0)';
        });
      };

      // Scroll handler with RAF throttling to avoid forced reflow
      let lastProgress = 0;
      let scrollTicking = false;
      let scrollBound = false;

      const handleScroll = () => {
        if (scrollTicking) return;
        scrollTicking = true;

        requestAnimationFrame(() => {
          const containerRect = pillarsContainer.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const containerCenter = containerRect.top + containerRect.height / 2;
          const viewportCenter = viewportHeight / 2;
          const progress = 1 - (containerCenter - viewportCenter) / viewportHeight;
          const clampedProgress = Math.max(0, Math.min(2, progress));
          scrollTicking = false;

          // Only update if progress changed significantly
          if (Math.abs(clampedProgress - lastProgress) < 0.001) return;
          lastProgress = clampedProgress;

          pillars.forEach(pillar => {
            const targetAngle = pillar._startAngle + (pillar._orbitSpeed * clampedProgress);
            const angleRad = (targetAngle * Math.PI) / 180;
            const newX = pillar._centerX + Math.cos(angleRad) * pillar._orbitRadius;
            const newY = pillar._centerY + Math.sin(angleRad) * pillar._orbitRadius;

            if (hasGsap) {
              gsap.to(pillar, {
                left: newX + unit,
                top: newY + unit,
                duration: 0.6,
                ease: 'power2.out',
                overwrite: 'auto'
              });
            } else {
              pillar.style.left = newX + unit;
              pillar.style.top = newY + unit;
            }
          });
        });
      };

      // Gate scroll handler with IntersectionObserver — only bind when visible
      this._pillarsVisibilityObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            initPillarParams();
            if (!scrollBound) {
              scrollBound = true;
              window.addEventListener('scroll', handleScroll, { passive: true });
              handleScroll();
            }
            // Clear will-change when not actively animating
          } else if (scrollBound) {
            window.removeEventListener('scroll', handleScroll);
            scrollBound = false;
            // Remove will-change to free compositor memory when off-screen
            pillars.forEach(p => { p.style.willChange = 'auto'; });
          }
        });
      }, { rootMargin: '100px 0px' });

      this._pillarsVisibilityObserver.observe(pillarsContainer);
      this._pillarsScrollHandler = handleScroll;
    }

    initSvgAnimation() {
      const svgContainers = this.querySelectorAll('.universal-image-text--svg[data-animate-svg="true"]');
      const mediaContainer = this.querySelector('.universal-image-text--media');

      if (!svgContainers.length) return;

      // Use Intersection Observer on the media container (always visible)
      // This ensures it triggers on both mobile and desktop
      const observeTarget = mediaContainer || this;

      this.svgObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Add is-visible to ALL svg containers (desktop and mobile)
            svgContainers.forEach(container => {
              container.classList.add('is-visible');

              // If GSAP is available, use it for more complex animations
              if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
                this.animateSvgWithGsap(container);
              }
            });

            // Unobserve after triggering (one-time animation)
            this.svgObserver.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.3,
        rootMargin: '0px 0px -100px 0px'
      });

      this.svgObserver.observe(observeTarget);
    }

    animateSvgWithGsap(svgContainer) {
      const svg = svgContainer.querySelector('svg');
      if (!svg) return;

      // Animate paths with draw effect
      const paths = svg.querySelectorAll('path.animate-draw, line.animate-draw, polyline.animate-draw');
      if (paths.length > 0) {
        paths.forEach((path, index) => {
          const length = path.getTotalLength ? path.getTotalLength() : 1000;
          gsap.set(path, {
            strokeDasharray: length,
            strokeDashoffset: length
          });
          gsap.to(path, {
            strokeDashoffset: 0,
            duration: 1.5,
            delay: index * 0.2,
            ease: 'power2.out'
          });
        });
      }

      // Animate data points / labels
      const fadeElements = svg.querySelectorAll('.animate-fade-up');
      if (fadeElements.length > 0) {
        gsap.from(fadeElements, {
          opacity: 0,
          y: 20,
          duration: 0.6,
          stagger: 0.2,
          ease: 'power2.out'
        });
      }
    }

    prepareAnimations() {
      let section = this;

      section.tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: "top bottom"
        }
      });

      document.fonts.ready.then(function() {
        const headings = section.querySelectorAll('.universal-image-text--heading');
        const paragraphs = section.querySelectorAll('.universal-image-text--body p');

        if (headings.length > 0 || paragraphs.length > 0) {
          section.splittext = new SplitText([...headings, ...paragraphs], {
            type: 'lines, words',
            linesClass: 'line-child'
          });
        }

        if (headings.length > 0) {
          const headingLines = section.querySelectorAll('.universal-image-text--heading .line-child div');
          let h_duration = 0.8 + ((headingLines.length - 1) * 0.08);

          section.tl
            .set(headings, {
              visibility: 'visible'
            }, 0)
            .from(headingLines, {
              duration: h_duration,
              yPercent: '100',
              stagger: 0.08
            }, 0);
        }

        if (paragraphs.length > 0) {
          const pLines = section.querySelectorAll('.universal-image-text--body p .line-child div');
          let p_duration = 0.8 + ((pLines.length - 1) * 0.02);

          section.tl
            .set(paragraphs, {
              visibility: 'visible'
            }, 0.3)
            .from(pLines, {
              duration: p_duration,
              yPercent: '100',
              stagger: 0.02
            }, 0.3);
        }
      });
    }
  }

  customElements.define('universal-image-text', UniversalImageText);
}
